import { Typography } from '@mui/material';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import { css } from '@emotion/react';
import { colors } from '../../../theme/colors';
import { FMT } from '../../../util/formatter-service';
import { ISourcingFundingRound, ISourcingProfileData } from '../data/sourcingProfileMockData';
import { MoreItemsTooltip } from './MoreItemsTooltip';

/**
 * Figma: `border/default/tooltip`. Kept local for the one style that still
 * uses it (the row divider); the overflow-chip treatment was moved to
 * `MoreItemsTooltip` and now owns its own copy of this token.
 */
const borderTooltip = '#D5D7EB';

const sidebarCss = css`
  padding: 12px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: sticky;
  top: 0;
`;

const digestTitleCss = css`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.15px;
  color: ${colors.text.main};
`;

const rowsColumnCss = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const rowCss = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 24px;
  width: 100%;
`;

const rowLeadCss = css`
  padding-top: 8px;
`;

const labelCss = css`
  font-size: 12px;
  font-weight: 400;
  line-height: 1.66;
  color: ${colors.text.secondary};
  width: 126px;
  flex-shrink: 0;
`;

const valueCss = css`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.43;
  letter-spacing: 0.17px;
  color: ${colors.text.main};
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const valueMutedCss = css`
  font-size: 12px;
  font-weight: 400;
  line-height: 1.66;
  color: ${colors.text.secondary};
  flex-shrink: 0;
`;

const valueRowFlexCss = css`
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
`;

const hqRowCss = css`
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
`;

/**
 * Inline avatar for the Acquired By row — mirrors the small logo treatment
 * used in the old Company Details panel so the acquirer reads as a company
 * rather than plain text.
 */
const companyLogoAvatarCss = css`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: white;
  box-shadow: 0px 4px 6px 0px rgba(16, 37, 62, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  color: ${colors.primary[100]};
  flex-shrink: 0;
  overflow: hidden;
`;

const dividerCss = css`
  width: 100%;
  height: 0;
  border: none;
  border-top: 0.5px solid ${borderTooltip};
  margin: 0;
`;

const chipStageLabelCss = css`
  font-size: 12px;
  font-weight: 400;
  line-height: 1.66;
  color: ${colors.secondary[60]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const chipStageCss = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  padding: 0 8px;
  border-radius: 100px;
  background: ${colors.secondary[10]};
  overflow: hidden;
`;

const chipsWrapCss = css`
  display: flex;
  flex-wrap: nowrap;
  gap: 4px;
  align-items: center;
  justify-content: flex-end;
  overflow: hidden;
  flex: 1;
  min-width: 0;
`;

const chipsWrapShrinkCss = css`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  justify-content: flex-end;
  overflow: hidden;
`;

interface OverviewSidebarProps {
  readonly data: ISourcingProfileData;
}

export function OverviewSidebar({ data }: OverviewSidebarProps) {
  const age = data.foundedYear != null ? new Date().getFullYear() - data.foundedYear : null;
  const totalRaisedFormatted =
    data.totalRaised != null ? FMT.format('currencyShort', data.totalRaised) : null;
  const fundingRoundsCount = data.fundingRounds.length;
  const investorsCount = aggregateInvestorCount(data.fundingRounds);

  return (
    <div css={sidebarCss}>
      <Typography component='h2' css={digestTitleCss}>
        Digest
      </Typography>

      <div css={rowsColumnCss}>
        {data.foundedYear != null && (
          <div css={rowCss}>
            <Typography css={labelCss}>Founded</Typography>
            <div css={valueRowFlexCss}>
              <Typography css={valueCss}>{data.foundedYear}</Typography>
              {age != null && <Typography css={valueMutedCss}>({age} yrs)</Typography>}
            </div>
          </div>
        )}

        {data.headcount != null && <TextMetricRow label='Headcount' value={data.headcount} />}

        {data.hq != null && (
          <div css={rowCss}>
            <Typography css={labelCss}>HQ</Typography>
            <div css={hqRowCss}>
              <LocationOnOutlined sx={{ fontSize: 16, color: colors.text.secondary }} aria-hidden />
              <Typography css={valueCss}>{data.hq}</Typography>
            </div>
          </div>
        )}

        {data.secondaryHq != null && (
          <div css={rowCss}>
            <Typography css={labelCss}>Secondary HQ</Typography>
            <div css={hqRowCss}>
              <LocationOnOutlined sx={{ fontSize: 16, color: colors.text.secondary }} aria-hidden />
              <Typography css={valueCss}>{data.secondaryHq}</Typography>
            </div>
          </div>
        )}

        <ChipMetricRow label='Sector' values={data.sector} />
        <ChipMetricRow label='Industry' values={data.industry} wideValue />

        {data.legalName != null && <TextMetricRow label='Legal Name' value={data.legalName} />}
        {data.companyType != null && <TextMetricRow label='Company Type' value={data.companyType} />}
        {data.alsoKnownAs.length > 0 && (
          <div css={rowCss}>
            <Typography css={labelCss}>Also Known As</Typography>
            <div css={valueRowFlexCss}>
              <Typography css={valueCss}>{data.alsoKnownAs[0]}</Typography>
              {data.alsoKnownAs.length > 1 && (
                <MoreItemsTooltip label='Aliases' items={data.alsoKnownAs.slice(1)} />
              )}
            </div>
          </div>
        )}
        {data.acquiredBy != null && (
          <div css={rowCss}>
            <Typography css={labelCss}>Acquired By</Typography>
            <div css={valueRowFlexCss}>
              <div css={companyLogoAvatarCss} aria-hidden>
                {data.acquiredBy[0]?.toUpperCase()}
              </div>
              <Typography css={valueCss}>{data.acquiredBy}</Typography>
            </div>
          </div>
        )}

        <hr css={dividerCss} />

        {totalRaisedFormatted != null && (
          <TextMetricRow label='Total Raised' value={totalRaisedFormatted} lead />
        )}

        <TextMetricRow label='Funding Rounds' value={String(fundingRoundsCount)} />
        <TextMetricRow label='Investors' value={String(investorsCount)} />
      </div>
    </div>
  );
}

function TextMetricRow({ label, value, lead = false }: { label: string; value: string; lead?: boolean }) {
  return (
    <div css={[rowCss, lead && rowLeadCss]}>
      <Typography css={labelCss}>{label}</Typography>
      <Typography css={valueCss}>{value}</Typography>
    </div>
  );
}

function ChipMetricRow({
  label,
  values,
  wideValue,
}: {
  label: string;
  values: readonly string[];
  wideValue?: boolean;
}) {
  if (values.length === 0) {
    return null;
  }
  const [first, ...rest] = values;
  const more = rest.length;

  return (
    <div css={rowCss}>
      <Typography css={labelCss}>{label}</Typography>
      <div css={wideValue ? chipsWrapCss : chipsWrapShrinkCss}>
        <span css={chipStageCss} title={first}>
          <span css={chipStageLabelCss}>{first}</span>
        </span>
        {more > 0 && <MoreItemsTooltip label={label} items={rest} />}
      </div>
    </div>
  );
}

function aggregateInvestorCount(rounds: readonly ISourcingFundingRound[]): number {
  const named = new Set<string>();
  let fromPlaceholders = 0;
  for (const round of rounds) {
    for (const entry of round.allInvestors) {
      const m = /^\+(\d+)$/.exec(entry);
      if (m) {
        fromPlaceholders += parseInt(m[1], 10);
      } else {
        named.add(entry);
      }
    }
  }
  return named.size + fromPlaceholders;
}
